import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  Tabs,
  Tab,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  SignatureCanvas,
} from '@mui/material';
import {
  Print,
  Download,
  FilterList,
  Refresh,
  Today,
  CalendarMonth,
  Person,
  Hotel,
  Receipt,
  Visibility,
  Description,
  Phone,
  Email,
  Room,
  DateRange,
  AttachMoney,
  LocalOffer,
  Discount,
  Badge,
  Business,
  CheckCircle,
  Payment,
  Edit,
} from '@mui/icons-material';
import axios from 'axios';
import Sidebar from '../../../../components/sidebars/sidebar_reception';
import TopNav from '../../../../components/TopNav_hotelStaff';

// API base URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const HOTEL_NAME = process.env.REACT_APP_HOTEL_NAME;
const HOTEL_ADDRESS = process.env.REACT_APP_HOTEL_ADDRESS;
const HOTEL_PHONE = process.env.REACT_APP_HOTEL_PHONE;
const HOTEL_EMAIL = process.env.REACT_APP_HOTEL_EMAIL || 'info@hotel.com';

const AccommodationSalesReport = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const printRef = useRef();
  const bookingPrintRef = useRef();
  const navigate = useNavigate();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  
  // Filter states
  const [filters, setFilters] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    staffId: '',
    bookingStatus: '',
    paymentStatus: '',
    paymentMethod: '',
    dateRange: 'today',
  });
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Dialog states
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [printBookingDialogOpen, setPrintBookingDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  // Navigation functions
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userProfile');
    navigate('/Entri');
  };

  const handleNotificationClick = () => {
    alert('You have new notifications!');
  };

  // Get hotel code from user context
  const getHotelCode = () => {
    const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
    return storedProfile.hotelcode || storedProfile.hotel_code || storedProfile.hotelCode || 'eHT5678042024';
  };

  // API service functions
  const api = {
    getBookings: async (params = {}) => {
      const response = await axios.get(`${API_BASE_URL}/hotel/staff/reception/index.php`, {
        params: { hotel_code: getHotelCode(), ...params }
      });
      return response.data;
    },
    getStaff: async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/hotel/staff/reception/staff.php`, {
          params: { hotel_code: getHotelCode() }
        });
        return response.data;
      } catch (error) {
        console.error('Error fetching staff:', error);
        return { success: false, staff: [] };
      }
    },
    getStatistics: async (params = {}) => {
      try {
        const response = await axios.get(`${API_BASE_URL}/hotel/staff/reception/statistics.php`, {
          params: { hotel_code: getHotelCode(), ...params }
        });
        return response.data;
      } catch (error) {
        console.error('Error fetching statistics:', error);
        return { success: false, statistics: {} };
      }
    }
  };

  useEffect(() => {
    loadData();
    loadStaff();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, bookings]);

  useEffect(() => {
    handleDateRangeChange(filters.dateRange);
  }, [filters.dateRange]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [bookingsData, statsData] = await Promise.all([
        api.getBookings(),
        api.getStatistics()
      ]);

      if (bookingsData.success) {
        setBookings(bookingsData.bookings || []);
      }

      if (statsData.success) {
        setStatistics(statsData.statistics || {});
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStaff = async () => {
    try {
      const result = await api.getStaff();
      if (result.success) {
        setStaffList(result.staff || []);
      }
    } catch (error) {
      console.error('Error loading staff:', error);
    }
  };

  const handleDateRangeChange = (range) => {
    const today = new Date();
    const startDate = new Date();
    
    switch (range) {
      case 'today':
        setFilters(prev => ({
          ...prev,
          startDate: today.toISOString().split('T')[0],
          endDate: today.toISOString().split('T')[0],
          dateRange: 'today'
        }));
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        setFilters(prev => ({
          ...prev,
          startDate: yesterday.toISOString().split('T')[0],
          endDate: yesterday.toISOString().split('T')[0],
          dateRange: 'yesterday'
        }));
        break;
      case 'thisWeek':
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        setFilters(prev => ({
          ...prev,
          startDate: startOfWeek.toISOString().split('T')[0],
          endDate: today.toISOString().split('T')[0],
          dateRange: 'thisWeek'
        }));
        break;
      case 'thisMonth':
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        setFilters(prev => ({
          ...prev,
          startDate: startOfMonth.toISOString().split('T')[0],
          endDate: today.toISOString().split('T')[0],
          dateRange: 'thisMonth'
        }));
        break;
      case 'lastMonth':
        const firstDayLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastDayLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        setFilters(prev => ({
          ...prev,
          startDate: firstDayLastMonth.toISOString().split('T')[0],
          endDate: lastDayLastMonth.toISOString().split('T')[0],
          dateRange: 'lastMonth'
        }));
        break;
      case 'custom':
        setFilters(prev => ({
          ...prev,
          dateRange: 'custom'
        }));
        break;
      default:
        break;
    }
  };

  const applyFilters = () => {
    let filtered = [...bookings];

    if (filters.startDate) {
      filtered = filtered.filter(booking => {
        const bookingDate = booking.created_at ? booking.created_at.split(' ')[0] : booking.check_in_date;
        return bookingDate >= filters.startDate;
      });
    }

    if (filters.endDate) {
      filtered = filtered.filter(booking => {
        const bookingDate = booking.created_at ? booking.created_at.split(' ')[0] : booking.check_in_date;
        return bookingDate <= filters.endDate;
      });
    }

    if (filters.staffId) {
      filtered = filtered.filter(booking => 
        booking.created_by === filters.staffId || booking.created_by_id === filters.staffId
      );
    }

    if (filters.bookingStatus) {
      filtered = filtered.filter(booking => 
        booking.booking_status === filters.bookingStatus
      );
    }

    if (filters.paymentStatus) {
      filtered = filtered.filter(booking => 
        booking.payment_status === filters.paymentStatus
      );
    }

    if (filters.paymentMethod) {
      filtered = filtered.filter(booking => 
        booking.payment_method === filters.paymentMethod
      );
    }

    setFilteredBookings(filtered);
    setPage(0);
  };

  const handleFilterChange = (field) => (event) => {
    setFilters(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleDateChange = (field) => (event) => {
    setFilters(prev => ({
      ...prev,
      [field]: event.target.value,
      dateRange: 'custom'
    }));
  };

  const clearFilters = () => {
    const today = new Date().toISOString().split('T')[0];
    setFilters({
      startDate: today,
      endDate: today,
      staffId: '',
      bookingStatus: '',
      paymentStatus: '',
      paymentMethod: '',
      dateRange: 'today',
    });
  };

  const handleViewDetails = (booking) => {
    setSelectedBooking(booking);
    setDetailsDialogOpen(true);
  };

  const handlePrintBooking = (booking) => {
    setSelectedBooking(booking);
    setPrintBookingDialogOpen(true);
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Accommodation Sales Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; color: #333; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
            .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-bottom: 30px; }
            .stat-card { border: 1px solid #ddd; padding: 15px; text-align: center; border-radius: 4px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px 12px; text-align: left; }
            th { background-color: #f5f5f5; font-weight: bold; }
            .footer { text-align: center; margin-top: 30px; font-size: 12px; color: #666; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const handlePrintIndividualBooking = () => {
    const booking = selectedBooking;
    if (!booking) return;

    // Helper function to safely format currency
    const formatCurrency = (amount) => {
        const num = parseFloat(amount || 0);
        return `₦${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    // Helper to calculate nights
    const calculateNights = (checkIn, checkOut) => {
        const checkInDate = new Date(checkIn);
        const checkOutDate = new Date(checkOut);
        const timeDiff = checkOutDate.getTime() - checkInDate.getTime();
        return Math.ceil(timeDiff / (1000 * 3600 * 24));
    };

    const nights = calculateNights(booking.check_in_date, booking.check_out_date);
    const originalAmount = parseFloat(booking.original_amount || booking.total_amount + (booking.discount_amount || 0));
    const totalAmount = parseFloat(booking.total_amount || 0);
    const discountAmount = parseFloat(booking.discount_amount || 0);
    const paidAmount = parseFloat(booking.paid_amount || 0);
    const balanceDue = totalAmount - paidAmount;

    // Determine status badge class
    const getPaymentStatusClass = (status) => {
        switch (status?.toLowerCase()) {
            case 'paid': return 'status-paid';
            case 'partial': return 'status-partial';
            default: return 'status-pending';
        }
    };

    const getBookingStatusClass = (status) => {
        switch (status?.toLowerCase()) {
            case 'confirmed': return 'status-confirmed';
            case 'checked_in': return 'status-checked-in';
            case 'checked_out': return 'status-checked-out';
            default: return 'status-pending';
        }
    };

    const printWindow = window.open('', '_blank');
    
    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice ${booking.booking_number}</title>
          <meta charset="UTF-8">
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            /* A4 Size Configuration */
            @page {
              size: A4;
              margin: 0;
            }
            
            body {
              font-family: 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
              background: white;
              padding: 0;
              margin: 0;
              color: #212529;
              line-height: 1.4;
            }
            
            /* Main container - fits within A4 */
            .invoice-container {
              max-width: 210mm; /* A4 width */
              min-height: 297mm; /* A4 height */
              margin: 0 auto;
              background: white;
              display: flex;
              flex-direction: column;
              page-break-after: avoid;
              page-break-inside: avoid;
            }
            
            /* Ensure content fits on one page */
            .invoice-content-wrapper {
              flex: 1;
              display: flex;
              flex-direction: column;
              height: 100%;
            }
            
            /* Header Section - Compact */
            .invoice-header {
              background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
              color: white;
              padding: 20px 25px;
              text-align: center;
            }
            
            .hotel-name {
              font-size: 24px;
              font-weight: 700;
              letter-spacing: 1px;
              margin-bottom: 5px;
            }
            
            .hotel-tagline {
              font-size: 11px;
              opacity: 0.85;
              margin-top: 3px;
            }
            
            .invoice-title {
              background: #f8f9fa;
              padding: 12px;
              text-align: center;
              border-bottom: 2px solid #e0e0e0;
            }
            
            .invoice-title h2 {
              color: #1e3c72;
              font-size: 18px;
              font-weight: 600;
              margin: 0;
            }
            
            .invoice-title p {
              color: #6c757d;
              font-size: 10px;
              margin-top: 3px;
            }
            
            /* Content - Compact spacing */
            .invoice-content {
              padding: 20px 25px;
              flex: 1;
            }
            
            /* Info Grid - Compact */
            .info-grid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 15px;
              margin-bottom: 15px;
            }
            
            .info-card {
              background: #f8f9fa;
              border-radius: 8px;
              padding: 12px;
            }
            
            .info-card h4 {
              color: #1e3c72;
              font-size: 12px;
              font-weight: 600;
              margin-bottom: 8px;
              border-left: 3px solid #1e3c72;
              padding-left: 8px;
            }
            
            .info-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 6px;
              font-size: 11px;
            }
            
            .info-label {
              color: #6c757d;
              font-weight: 500;
            }
            
            .info-value {
              color: #212529;
              font-weight: 500;
            }
            
            /* Status Badges - Small */
            .badge {
              display: inline-block;
              padding: 2px 8px;
              border-radius: 12px;
              font-size: 10px;
              font-weight: 600;
              text-transform: uppercase;
            }
            
            .status-paid { background: #d1e7dd; color: #0a3622; }
            .status-partial { background: #fff3cd; color: #997404; }
            .status-pending { background: #f8d7da; color: #842029; }
            .status-confirmed { background: #cfe2ff; color: #084298; }
            .status-checked-in { background: #cff4fc; color: #055160; }
            .status-checked-out { background: #e2e3e5; color: #41464b; }
            
            /* Price Table - Compact */
            .price-table {
              background: #f8f9fa;
              border-radius: 8px;
              margin: 15px 0;
              overflow: hidden;
            }
            
            .price-row {
              display: flex;
              justify-content: space-between;
              padding: 8px 15px;
              border-bottom: 1px solid #dee2e6;
              font-size: 12px;
            }
            
            .price-row:last-child {
              border-bottom: none;
            }
            
            .price-row.total {
              background: #1e3c72;
              color: white;
              font-weight: 700;
              font-size: 14px;
              padding: 10px 15px;
            }
            
            .price-row.discount {
              background: #fff8e7;
              color: #b85c00;
            }
            
            .price-label {
              font-weight: 600;
            }
            
            .price-amount {
              font-weight: 600;
            }
            
            /* Staff Section - Compact */
            .staff-section {
              background: #e9ecef;
              border-radius: 8px;
              padding: 10px 15px;
              margin: 15px 0 10px 0;
            }
            
            .staff-section h4 {
              font-size: 11px;
              font-weight: 600;
              color: #495057;
              margin-bottom: 8px;
            }
            
            .staff-details {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 6px;
              font-size: 10px;
            }
            
            /* Signature - Compact */
            .signature-area {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 30px;
              margin-top: 20px;
              padding-top: 15px;
            }
            
            .signature-line {
              border-top: 1px solid #adb5bd;
              margin-top: 25px;
              padding-top: 6px;
              text-align: center;
            }
            
            .signature-line p {
              font-size: 10px;
              color: #6c757d;
              margin: 0;
            }
            
            /* Footer - Compact */
            .invoice-footer {
              background: #f8f9fa;
              text-align: center;
              padding: 12px;
              font-size: 9px;
              color: #6c757d;
              border-top: 1px solid #dee2e6;
              margin-top: auto;
            }
            
            /* Utility */
            .text-right { text-align: right; }
            .mb-2 { margin-bottom: 8px; }
            .mt-3 { margin-top: 12px; }
            .divider { height: 1px; background: #dee2e6; margin: 15px 0; }
            
            /* Print-specific styles for A4 */
            @media print {
              @page {
                size: A4;
                margin: 0;
              }
              
              body {
                background: white;
                padding: 0;
                margin: 0;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              
              .invoice-container {
                box-shadow: none;
                border-radius: 0;
                max-width: 100%;
                min-height: auto;
              }
              
              .no-print {
                display: none;
              }
              
              /* Ensure everything stays on one page */
              .invoice-content-wrapper {
                page-break-inside: avoid;
                page-break-after: avoid;
              }
              
              /* Prevent breaks inside critical sections */
              .info-grid, .price-table, .staff-section, .signature-area {
                page-break-inside: avoid;
              }
            }
            
            /* Screen styles for preview */
            @media screen {
              body {
                background: #e9ecef;
                padding: 20px;
              }
              .invoice-container {
                margin: 0 auto;
                box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
              }
            }
          </style>
        </head>
        <body>
          <div class="invoice-container">
            <div class="invoice-content-wrapper">
              <!-- Header -->
              <div class="invoice-header">
                <div class="hotel-name">${HOTEL_NAME || 'LUXURY HOTEL'}</div>
                <div class="hotel-tagline">${HOTEL_ADDRESS || 'Premium Hospitality'}</div>
                <div class="hotel-tagline">📞 ${HOTEL_PHONE || 'N/A'} | ✉️ ${HOTEL_EMAIL}</div>
              </div>
              
              <div class="invoice-title">
                <h2>BOOKING RECEIPT / INVOICE</h2>
                <p>Booking Confirmation & Payment Summary</p>
              </div>
              
              <div class="invoice-content">
                <!-- Two Column Info -->
                <div class="info-grid">
                  <div class="info-card">
                    <h4>📋 BOOKING INFO</h4>
                    <div class="info-row">
                      <span class="info-label">Booking Number:</span>
                      <span class="info-value"><strong>${booking.booking_number || 'N/A'}</strong></span>
                    </div>
                    <div class="info-row">
                      <span class="info-label">Booking Date:</span>
                      <span class="info-value">${booking.created_at ? booking.created_at.split(' ')[0] : booking.check_in_date}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-label">Status:</span>
                      <span class="info-value"><span class="badge ${getBookingStatusClass(booking.booking_status)}">${booking.booking_status || 'N/A'}</span></span>
                    </div>
                  </div>
                  
                  <div class="info-card">
                    <h4>👤 GUEST DETAILS</h4>
                    <div class="info-row">
                      <span class="info-label">Name:</span>
                      <span class="info-value"><strong>${booking.guest_name || 'N/A'}</strong></span>
                    </div>
                    <div class="info-row">
                      <span class="info-label">Email:</span>
                      <span class="info-value">${booking.email || 'N/A'}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-label">Phone:</span>
                      <span class="info-value">${booking.phone || 'N/A'}</span>
                    </div>
                  </div>
                </div>
                
                <!-- Stay Details -->
                <div class="info-card" style="margin-bottom: 15px;">
                  <h4>🏨 STAY DETAILS</h4>
                  <div class="info-row">
                    <span class="info-label">Room Type:</span>
                    <span class="info-value"><strong>${booking.room_type || 'N/A'}</strong> (Room ${booking.room_number || 'N/A'})</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Check-in:</span>
                    <span class="info-value">${booking.check_in_date || 'N/A'}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Check-out:</span>
                    <span class="info-value">${booking.check_out_date || 'N/A'}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Duration:</span>
                    <span class="info-value">${nights} night(s) | Guests: ${booking.number_of_guests || 1}</span>
                  </div>
                </div>
                
                <!-- Payment Breakdown -->
                <div class="price-table">
                  ${discountAmount > 0 ? `
                    <div class="price-row discount">
                      <span class="price-label">💰 Original Amount:</span>
                      <span class="price-amount">${formatCurrency(originalAmount)}</span>
                    </div>
                    <div class="price-row discount">
                      <span class="price-label">🏷️ Discount (${booking.discount_percentage || 0}%):</span>
                      <span class="price-amount">- ${formatCurrency(discountAmount)}</span>
                    </div>
                    ${booking.discount_reason ? `
                    <div class="price-row discount" style="font-size: 10px; padding-top: 0;">
                      <span class="price-label">Reason:</span>
                      <span class="price-amount">${booking.discount_reason}</span>
                    </div>
                    ` : ''}
                  ` : ''}
                  
                  <div class="price-row">
                    <span class="price-label">🏠 Room Charges:</span>
                    <span class="price-amount">${formatCurrency(totalAmount)}</span>
                  </div>
                  
                  <div class="price-row" style="background: #e7f1ff;">
                    <span class="price-label">💵 Amount Paid:</span>
                    <span class="price-amount" style="color: #0a5c2e;">${formatCurrency(paidAmount)}</span>
                  </div>
                  
                  <div class="price-row total">
                    <span class="price-label">💰 BALANCE DUE:</span>
                    <span class="price-amount">${formatCurrency(balanceDue)}</span>
                  </div>
                </div>
                
                <div class="info-row" style="margin-bottom: 12px; padding: 0 5px;">
                  <span class="info-label">Payment Method:</span>
                  <span class="info-value"><strong>${booking.payment_method || 'Not specified'}</strong></span>
                  <span class="info-label">Payment Status:</span>
                  <span class="info-value"><span class="badge ${getPaymentStatusClass(booking.payment_status)}">${booking.payment_status || 'pending'}</span></span>
                </div>
                
                <!-- Staff Info -->
                <div class="staff-section">
                  <h4>👨‍💼 PROCESSED BY</h4>
                  <div class="staff-details">
                    <div><strong>Name:</strong> ${booking.created_by_name || 'System'}</div>
                    <div><strong>Staff ID:</strong> ${booking.created_by_id || 'N/A'}</div>
                    <div><strong>Department:</strong> ${booking.created_by_department || 'Reception'}</div>
                    <div><strong>Date:</strong> ${new Date().toLocaleDateString()}</div>
                  </div>
                </div>
                
                <!-- Signature -->
                <div class="signature-area">
                  <div class="signature-line">
                    <p>Guest Signature</p>
                  </div>
                  <div class="signature-line">
                    <p>Staff Signature</p>
                  </div>
                </div>
              </div>
              
              <!-- Footer -->
              <div class="invoice-footer">
                <p>Thank you for choosing ${HOTEL_NAME || 'us'}! We hope you had a pleasant stay.</p>
                <p>For inquiries or support, please contact our front desk.</p>
                <p style="margin-top: 5px;">Generated on ${new Date().toLocaleString()}</p>
                <p style="margin-top: 3px; font-size: 8px;">** This is a computer-generated document, no signature required. **</p>
              </div>
            </div>
          </div>
          
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                setTimeout(function() { window.close(); }, 500);
              }, 200);
            };
          </script>
        </body>
      </html>
    `;
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
};

  const handleExport = () => {
    const csvContent = generateCSV();
    downloadCSV(csvContent, `accommodation-sales-${new Date().toISOString().split('T')[0]}.csv`);
  };

  const generateCSV = () => {
    const headers = ['Booking Number', 'Guest Name', 'Room Type', 'Room Number', 'Check-in', 'Check-out', 'Nights', 'Total Amount', 'Paid Amount', 'Discount Amount', 'Payment Status', 'Booking Status', 'Payment Method', 'Created By', 'Booking Date'];
    
    const rows = filteredBookings.map(booking => [
      `"${booking.booking_number}"`,
      `"${booking.guest_name}"`,
      `"${booking.room_type}"`,
      `"${booking.room_number}"`,
      `"${booking.check_in_date}"`,
      `"${booking.check_out_date}"`,
      booking.nights || 0,
      booking.total_amount,
      booking.paid_amount || 0,
      booking.discount_amount || 0,
      `"${booking.payment_status}"`,
      `"${booking.booking_status}"`,
      `"${booking.payment_method}"`,
      `"${booking.created_by_name || booking.created_by}"`,
      `"${booking.created_at ? booking.created_at.split(' ')[0] : booking.check_in_date}"`
    ]);

    return [headers, ...rows].map(row => row.join(',')).join('\n');
  };

  const downloadCSV = (content, filename) => {
    const blob = new Blob([content], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const calculateStats = () => {
    const totalRevenue = filteredBookings.reduce((sum, booking) => sum + parseFloat(booking.total_amount || 0), 0);
    const totalPaid = filteredBookings.reduce((sum, booking) => sum + parseFloat(booking.paid_amount || 0), 0);
    const totalDiscount = filteredBookings.reduce((sum, booking) => sum + parseFloat(booking.discount_amount || 0), 0);
    const confirmedBookings = filteredBookings.filter(b => b.booking_status === 'confirmed').length;
    const checkedInBookings = filteredBookings.filter(b => b.booking_status === 'checked_in').length;
    
    return {
      totalRevenue,
      totalPaid,
      totalDiscount,
      confirmedBookings,
      checkedInBookings,
      totalBookings: filteredBookings.length
    };
  };

  const stats = calculateStats();

  const getStatusColor = (status) => {
    const statusColors = {
      confirmed: 'success',
      checked_in: 'info',
      checked_out: 'secondary',
      cancelled: 'error',
      pending: 'warning',
      paid: 'success',
      partial: 'info',
    };
    return statusColors[status] || 'default';
  };

  const calculateNights = (checkIn, checkOut) => {
    const checkInDate = new Date(checkIn);
    const checkOutDate = new Date(checkOut);
    const timeDiff = checkOutDate.getTime() - checkInDate.getTime();
    return Math.ceil(timeDiff / (1000 * 3600 * 24));
  };

  const getDateRangeText = () => {
    switch (filters.dateRange) {
      case 'today': return "Today's Transactions";
      case 'yesterday': return "Yesterday's Transactions";
      case 'thisWeek': return "This Week's Transactions";
      case 'thisMonth': return "This Month's Transactions";
      case 'lastMonth': return "Last Month's Transactions";
      case 'custom': return `Transactions from ${filters.startDate} to ${filters.endDate}`;
      default: return "Today's Transactions";
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
          mt: 0,
          maxWidth: '1400px',
          mx: 'auto',
        }}
      >
        <TopNav
          onLogout={handleLogout}
          onNotificationClick={handleNotificationClick}
          toggleSidebar={toggleSidebar}
        />

        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Paper elevation={2} sx={{ p: 3, mb: 3, background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`, color: 'white' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  Accommodation Sales Report
                </Typography>
                <Typography variant="subtitle1">
                  {getDateRangeText()}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button variant="contained" startIcon={<Print />} onClick={handlePrint} sx={{ backgroundColor: 'white', color: 'primary.main' }}>
                  Print Report
                </Button>
                <Button variant="contained" startIcon={<Download />} onClick={handleExport} sx={{ backgroundColor: 'white', color: 'primary.main' }}>
                  Export CSV
                </Button>
                <Button variant="outlined" startIcon={<FilterList />} onClick={() => setFilterDialogOpen(true)} sx={{ borderColor: 'white', color: 'white' }}>
                  Filters
                </Button>
                <Button variant="outlined" startIcon={<Refresh />} onClick={loadData} disabled={loading} sx={{ borderColor: 'white', color: 'white' }}>
                  Refresh
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Date Range Quick Filters */}
          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Quick Date Filters</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
              <Button variant={filters.dateRange === 'today' ? 'contained' : 'outlined'} startIcon={<Today />} onClick={() => handleDateRangeChange('today')} size="small">Today</Button>
              <Button variant={filters.dateRange === 'yesterday' ? 'contained' : 'outlined'} startIcon={<CalendarMonth />} onClick={() => handleDateRangeChange('yesterday')} size="small">Yesterday</Button>
              <Button variant={filters.dateRange === 'thisWeek' ? 'contained' : 'outlined'} startIcon={<CalendarMonth />} onClick={() => handleDateRangeChange('thisWeek')} size="small">This Week</Button>
              <Button variant={filters.dateRange === 'thisMonth' ? 'contained' : 'outlined'} startIcon={<CalendarMonth />} onClick={() => handleDateRangeChange('thisMonth')} size="small">This Month</Button>
              <Button variant={filters.dateRange === 'lastMonth' ? 'contained' : 'outlined'} startIcon={<CalendarMonth />} onClick={() => handleDateRangeChange('lastMonth')} size="small">Last Month</Button>
              <Button variant={filters.dateRange === 'custom' ? 'contained' : 'outlined'} startIcon={<CalendarMonth />} onClick={() => handleDateRangeChange('custom')} size="small">Custom Range</Button>
            </Stack>
          </Paper>

          {/* Statistics Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={3}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">Total Revenue</Typography>
                      <Typography variant="h4" color="primary">₦{stats.totalRevenue.toLocaleString()}</Typography>
                    </Box>
                    <Receipt sx={{ fontSize: 40, color: 'primary.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={3}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">Amount Collected</Typography>
                      <Typography variant="h4" color="success.main">₦{stats.totalPaid.toLocaleString()}</Typography>
                    </Box>
                    <Download sx={{ fontSize: 40, color: 'success.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={3}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">Discounts Given</Typography>
                      <Typography variant="h4" color="warning.main">₦{stats.totalDiscount.toLocaleString()}</Typography>
                    </Box>
                    <LocalOffer sx={{ fontSize: 40, color: 'warning.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={3}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">Total Bookings</Typography>
                      <Typography variant="h4" color="info.main">{stats.totalBookings}</Typography>
                    </Box>
                    <Hotel sx={{ fontSize: 40, color: 'info.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Active Filters */}
          {(filters.staffId || filters.bookingStatus || filters.paymentStatus || filters.paymentMethod) && (
            <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Active Filters:</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                {filters.staffId && <Chip label={`Staff: ${staffList.find(s => s.staff_id === filters.staffId)?.first_name || filters.staffId}`} size="small" onDelete={() => handleFilterChange('staffId')({ target: { value: '' } })} />}
                {filters.bookingStatus && <Chip label={`Status: ${filters.bookingStatus}`} size="small" onDelete={() => handleFilterChange('bookingStatus')({ target: { value: '' } })} />}
                {filters.paymentStatus && <Chip label={`Payment: ${filters.paymentStatus}`} size="small" onDelete={() => handleFilterChange('paymentStatus')({ target: { value: '' } })} />}
                {filters.paymentMethod && <Chip label={`Method: ${filters.paymentMethod}`} size="small" onDelete={() => handleFilterChange('paymentMethod')({ target: { value: '' } })} />}
                <Button size="small" onClick={clearFilters}>Clear All</Button>
              </Stack>
            </Paper>
          )}

          {/* Bookings Table */}
          <Paper elevation={3}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: theme.palette.primary.main }}>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Booking #</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Guest</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Room</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Check-in/out</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Amount</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Payment</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Staff</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredBookings
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((booking) => (
                        <TableRow key={booking.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">{booking.booking_number}</Typography>
                            <Typography variant="caption" color="textSecondary">{booking.created_at ? booking.created_at.split(' ')[0] : booking.check_in_date}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">{booking.guest_name}</Typography>
                            <Typography variant="caption" color="textSecondary">{booking.email}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{booking.room_type}</Typography>
                            <Typography variant="caption" color="textSecondary">Room {booking.room_number}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{booking.check_in_date}</Typography>
                            <Typography variant="caption" color="textSecondary">to {booking.check_out_date}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold" color="primary">₦{parseFloat(booking.total_amount).toLocaleString()}</Typography>
                            {booking.discount_amount > 0 && <Typography variant="caption" color="warning.main">Saved: ₦{booking.discount_amount}</Typography>}
                          </TableCell>
                          <TableCell><Chip label={booking.booking_status} color={getStatusColor(booking.booking_status)} size="small" /></TableCell>
                          <TableCell>
                            <Chip label={booking.payment_status} color={getStatusColor(booking.payment_status)} size="small" />
                            {booking.payment_method && <Typography variant="caption">{booking.payment_method}</Typography>}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{booking.created_by_name || 'System'}</Typography>
                            <Typography variant="caption" color="textSecondary">{booking.created_by_department || ''}</Typography>
                          </TableCell>
                          <TableCell>
                            <IconButton size="small" color="primary" onClick={() => handleViewDetails(booking)} title="View Details"><Visibility /></IconButton>
                            <IconButton size="small" color="secondary" onClick={() => handlePrintBooking(booking)} title="Print Invoice"><Description /></IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {filteredBookings.length === 0 && (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h6" color="textSecondary">No bookings found for selected date range</Typography>
                  </Box>
                )}

                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={filteredBookings.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(event, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                />
              </>
            )}
          </Paper>

          {/* Filter Dialog */}
          <Dialog open={filterDialogOpen} onClose={() => setFilterDialogOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterList color="primary" />
                <Typography variant="h6">Filter Report</Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Start Date" type="date" value={filters.startDate} onChange={handleDateChange('startDate')} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="End Date" type="date" value={filters.endDate} onChange={handleDateChange('endDate')} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Staff Member</InputLabel>
                    <Select value={filters.staffId} onChange={handleFilterChange('staffId')} label="Staff Member">
                      <MenuItem value="">All Staff</MenuItem>
                      {staffList.map((staff) => (
                        <MenuItem key={staff.staff_id} value={staff.staff_id}>{staff.first_name} {staff.other_names} ({staff.staff_id})</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Booking Status</InputLabel>
                    <Select value={filters.bookingStatus} onChange={handleFilterChange('bookingStatus')} label="Booking Status">
                      <MenuItem value="">All Statuses</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="confirmed">Confirmed</MenuItem>
                      <MenuItem value="checked_in">Checked In</MenuItem>
                      <MenuItem value="checked_out">Checked Out</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Payment Status</InputLabel>
                    <Select value={filters.paymentStatus} onChange={handleFilterChange('paymentStatus')} label="Payment Status">
                      <MenuItem value="">All Statuses</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="paid">Paid</MenuItem>
                      <MenuItem value="partial">Partial</MenuItem>
                      <MenuItem value="refunded">Refunded</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Payment Method</InputLabel>
                    <Select value={filters.paymentMethod} onChange={handleFilterChange('paymentMethod')} label="Payment Method">
                      <MenuItem value="">All Methods</MenuItem>
                      <MenuItem value="Cash">Cash</MenuItem>
                      <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                      <MenuItem value="POS Terminal">POS Terminal</MenuItem>
                      <MenuItem value="paystack">Paystack</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={clearFilters}>Clear All</Button>
              <Button onClick={() => setFilterDialogOpen(false)}>Cancel</Button>
              <Button variant="contained" onClick={() => setFilterDialogOpen(false)}>Apply Filters</Button>
            </DialogActions>
          </Dialog>

          {/* Booking Details Dialog */}
          <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle>
              <Typography variant="h6">Booking Details</Typography>
            </DialogTitle>
            <DialogContent>
              {selectedBooking && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">Booking Number</Typography>
                    <Typography variant="body1" gutterBottom>{selectedBooking.booking_number}</Typography>
                    
                    <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>Guest Information</Typography>
                    <Typography variant="body1">{selectedBooking.guest_name}</Typography>
                    <Typography variant="body2" color="textSecondary">{selectedBooking.email}</Typography>
                    <Typography variant="body2" color="textSecondary">{selectedBooking.phone}</Typography>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">Room Information</Typography>
                    <Typography variant="body1">{selectedBooking.room_type}</Typography>
                    <Typography variant="body2" color="textSecondary">Room {selectedBooking.room_number}</Typography>
                    
                    <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>Stay Duration</Typography>
                    <Typography variant="body1">{selectedBooking.check_in_date} to {selectedBooking.check_out_date}</Typography>
                    <Typography variant="body2" color="textSecondary">{calculateNights(selectedBooking.check_in_date, selectedBooking.check_out_date)} nights</Typography>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">Financial Details</Typography>
                    {selectedBooking.original_amount > selectedBooking.total_amount && (
                      <Box sx={{ mb: 2, p: 1, bgcolor: '#fff3e0', borderRadius: 1 }}>
                        <Typography variant="caption" color="warning.main">Discount Applied!</Typography>
                        <Typography variant="body2">Original: ₦{parseFloat(selectedBooking.original_amount).toLocaleString()}</Typography>
                        <Typography variant="body2">Discount: {selectedBooking.discount_percentage}% (₦{parseFloat(selectedBooking.discount_amount).toLocaleString()})</Typography>
                        <Typography variant="body2">Reason: {selectedBooking.discount_reason || 'N/A'}</Typography>
                        <Typography variant="caption">Approved by: {selectedBooking.discount_approved_by || 'N/A'}</Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Total Amount:</Typography>
                      <Typography variant="body2" fontWeight="bold">₦{parseFloat(selectedBooking.total_amount).toLocaleString()}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Paid Amount:</Typography>
                      <Typography variant="body2" color="success.main">₦{parseFloat(selectedBooking.paid_amount || 0).toLocaleString()}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Balance:</Typography>
                      <Typography variant="body2" color="warning.main">₦{(parseFloat(selectedBooking.total_amount) - parseFloat(selectedBooking.paid_amount || 0)).toLocaleString()}</Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">Staff Information</Typography>
                    <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                      <Typography variant="body2"><strong>Name:</strong> {selectedBooking.created_by_name || 'System'}</Typography>
                      <Typography variant="body2"><strong>Staff ID:</strong> {selectedBooking.created_by_id || 'N/A'}</Typography>
                      <Typography variant="body2"><strong>Department:</strong> {selectedBooking.created_by_department || 'N/A'}</Typography>
                      <Typography variant="body2"><strong>Email:</strong> {selectedBooking.created_by_email || 'N/A'}</Typography>
                      <Typography variant="body2"><strong>Phone:</strong> {selectedBooking.created_by_phone || 'N/A'}</Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Status Information</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <Chip label={selectedBooking.booking_status} color={getStatusColor(selectedBooking.booking_status)} size="small" />
                      <Chip label={selectedBooking.payment_status} color={getStatusColor(selectedBooking.payment_status)} size="small" />
                    </Box>
                    <Typography variant="body2">Payment Method: {selectedBooking.payment_method || 'Not specified'}</Typography>
                  </Grid>
                </Grid>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
              <Button variant="contained" startIcon={<Print />} onClick={() => {
                setDetailsDialogOpen(false);
                handlePrintBooking(selectedBooking);
              }}>Print Invoice</Button>
            </DialogActions>
          </Dialog>

          {/* Print Booking Dialog - Modern Receipt */}
         {/* Print Booking Dialog - Optimized Single-Page Receipt */}
<Dialog open={printBookingDialogOpen} onClose={() => setPrintBookingDialogOpen(false)} maxWidth="sm" fullWidth>
  <DialogTitle>
    <Typography variant="h6">Print Booking Receipt</Typography>
  </DialogTitle>
  <DialogContent>
    <Box ref={bookingPrintRef}>
      {selectedBooking && (
        <div style={{ 
          fontFamily: 'Arial, sans-serif', 
          maxWidth: '650px', 
          margin: '0 auto',
          backgroundColor: '#fff',
          fontSize: '12px'
        }}>
          {/* Header - Simplified without large images */}
          <div style={{ 
            textAlign: 'center', 
            padding: '15px 20px',
            backgroundColor: '#1a237e',
            color: 'white'
          }}>
            <h2 style={{ margin: '0 0 5px 0', fontSize: '20px' }}>{HOTEL_NAME || 'LUXURY HOTEL'}</h2>
            <p style={{ margin: '2px 0', fontSize: '10px' }}>{HOTEL_ADDRESS || 'Hotel Address'}</p>
            <p style={{ margin: '2px 0', fontSize: '10px' }}>Tel: {HOTEL_PHONE || '+1234567890'} | Email: {HOTEL_EMAIL}</p>
          </div>

          {/* Receipt Title */}
          <div style={{ 
            textAlign: 'center', 
            padding: '10px',
            borderBottom: '2px solid #1a237e',
            marginBottom: '15px'
          }}>
            <h3 style={{ margin: 0, color: '#1a237e', fontSize: '16px' }}>BOOKING RECEIPT / INVOICE</h3>
            <p style={{ margin: '3px 0 0', fontSize: '10px', color: '#666' }}>Booking Confirmation & Payment Receipt</p>
          </div>

          {/* Content with tighter spacing */}
          <div style={{ padding: '0 15px' }}>
            
            {/* Booking Info - 2 column layout */}
            <div style={{ 
              marginBottom: '12px', 
              borderBottom: '1px solid #ddd', 
              paddingBottom: '10px'
            }}>
              <h4 style={{ fontSize: '13px', color: '#1a237e', margin: '0 0 8px 0', fontWeight: 'bold' }}>BOOKING INFORMATION</h4>
              <table style={{ width: '100%', fontSize: '11px' }}>
                <tbody>
                  <tr>
                    <td style={{ padding: '3px 0', width: '50%' }}><strong>Booking Number:</strong></td>
                    <td style={{ padding: '3px 0' }}>{selectedBooking.booking_number}</td>
                    <td style={{ padding: '3px 0', width: '50%' }}><strong>Booking Date:</strong></td>
                    <td style={{ padding: '3px 0' }}>{selectedBooking.created_at ? selectedBooking.created_at.split(' ')[0] : selectedBooking.check_in_date}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '3px 0' }}><strong>Booking Status:</strong></td>
                    <td style={{ padding: '3px 0' }}>
                      <span style={{ backgroundColor: '#d1ecf1', padding: '2px 6px', borderRadius: '3px', fontSize: '10px' }}>
                        {selectedBooking.booking_status?.toUpperCase()}
                      </span>
                    </td>
                    <td style={{ padding: '3px 0' }}><strong>Payment Status:</strong></td>
                    <td style={{ padding: '3px 0' }}>
                      <span style={{ 
                        backgroundColor: selectedBooking.payment_status === 'paid' ? '#d4edda' : '#fff3cd', 
                        padding: '2px 6px', 
                        borderRadius: '3px', 
                        fontSize: '10px',
                        color: selectedBooking.payment_status === 'paid' ? '#155724' : '#856404'
                      }}>
                        {selectedBooking.payment_status?.toUpperCase()}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Guest Information */}
            <div style={{ marginBottom: '12px', borderBottom: '1px solid #ddd', paddingBottom: '10px' }}>
              <h4 style={{ fontSize: '13px', color: '#1a237e', margin: '0 0 8px 0', fontWeight: 'bold' }}>GUEST INFORMATION</h4>
              <table style={{ width: '100%', fontSize: '11px' }}>
                <tbody>
                  <tr>
                    <td style={{ padding: '3px 0', width: '25%' }}><strong>Name:</strong></td>
                    <td style={{ padding: '3px 0', width: '75%' }} colSpan="3">{selectedBooking.guest_name}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '3px 0' }}><strong>Email:</strong></td>
                    <td style={{ padding: '3px 0' }}>{selectedBooking.email}</td>
                    <td style={{ padding: '3px 0' }}><strong>Phone:</strong></td>
                    <td style={{ padding: '3px 0' }}>{selectedBooking.phone}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Room & Stay Details */}
            <div style={{ marginBottom: '12px', borderBottom: '1px solid #ddd', paddingBottom: '10px' }}>
              <h4 style={{ fontSize: '13px', color: '#1a237e', margin: '0 0 8px 0', fontWeight: 'bold' }}>ACCOMMODATION DETAILS</h4>
              <table style={{ width: '100%', fontSize: '11px' }}>
                <tbody>
                  <tr>
                    <td style={{ padding: '3px 0', width: '33%' }}><strong>Room Type:</strong></td>
                    <td style={{ padding: '3px 0', width: '67%' }}>{selectedBooking.room_type}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '3px 0' }}><strong>Room Number:</strong></td>
                    <td style={{ padding: '3px 0' }}>{selectedBooking.room_number}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '3px 0' }}><strong>Check-in:</strong></td>
                    <td style={{ padding: '3px 0' }}>{selectedBooking.check_in_date}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '3px 0' }}><strong>Check-out:</strong></td>
                    <td style={{ padding: '3px 0' }}>{selectedBooking.check_out_date}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '3px 0' }}><strong>Nights:</strong></td>
                    <td style={{ padding: '3px 0' }}>{calculateNights(selectedBooking.check_in_date, selectedBooking.check_out_date)} nights</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '3px 0' }}><strong>Guests:</strong></td>
                    <td style={{ padding: '3px 0' }}>{selectedBooking.number_of_guests} person(s)</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Payment Breakdown */}
            <div style={{ marginBottom: '12px' }}>
              <h4 style={{ fontSize: '13px', color: '#1a237e', margin: '0 0 8px 0', fontWeight: 'bold' }}>PAYMENT BREAKDOWN</h4>
              
              {/* Discount Section - Compact */}
              {selectedBooking.discount_amount > 0 && (
                <div style={{ 
                  backgroundColor: '#fff3e0', 
                  padding: '8px', 
                  borderRadius: '4px', 
                  marginBottom: '10px',
                  fontSize: '11px'
                }}>
                  <table style={{ width: '100%' }}>
                    <tbody>
                      <tr>
                        <td><strong>Original Amount:</strong></td>
                        <td align="right">₦{parseFloat(selectedBooking.original_amount || selectedBooking.total_amount + (selectedBooking.discount_amount || 0)).toLocaleString()}</td>
                      </tr>
                      <tr style={{ color: '#e65100' }}>
                        <td><strong>Discount ({selectedBooking.discount_percentage}%):</strong></td>
                        <td align="right">-₦{parseFloat(selectedBooking.discount_amount).toLocaleString()}</td>
                      </tr>
                      <tr>
                        <td><strong>Reason:</strong></td>
                        <td align="right">{selectedBooking.discount_reason || 'N/A'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {/* Amount Summary */}
              <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr>
                    <td style={{ padding: '6px 0', borderBottom: '1px solid #eee' }}><strong>Room Charges:</strong></td>
                    <td align="right" style={{ padding: '6px 0', borderBottom: '1px solid #eee' }}>₦{parseFloat(selectedBooking.total_amount).toLocaleString()}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '6px 0', borderBottom: '1px solid #eee' }}><strong>Amount Paid:</strong></td>
                    <td align="right" style={{ padding: '6px 0', borderBottom: '1px solid #eee', color: '#28a745' }}>₦{parseFloat(selectedBooking.paid_amount || 0).toLocaleString()}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '8px 0', borderTop: '2px solid #333', fontWeight: 'bold' }}><strong>Balance Due:</strong></td>
                    <td align="right" style={{ padding: '8px 0', borderTop: '2px solid #333', fontWeight: 'bold', color: '#e65100', fontSize: '14px' }}>
                      ₦{(parseFloat(selectedBooking.total_amount) - parseFloat(selectedBooking.paid_amount || 0)).toLocaleString()}
                    </td>
                  </tr>
                </tbody>
              </table>
              
              <div style={{ marginTop: '8px', paddingTop: '5px', borderTop: '1px dashed #ccc', fontSize: '10px' }}>
                <strong>Payment Method:</strong> {selectedBooking.payment_method || 'Not specified'}
              </div>
            </div>

            {/* Staff Information - Compact */}
            <div style={{ 
              marginBottom: '12px', 
              backgroundColor: '#e8eaf6', 
              padding: '8px 10px', 
              borderRadius: '4px'
            }}>
              <h4 style={{ fontSize: '12px', color: '#1a237e', margin: '0 0 5px 0', fontWeight: 'bold' }}>PROCESSED BY</h4>
              <table style={{ width: '100%', fontSize: '11px' }}>
                <tbody>
                  <tr>
                    <td style={{ width: '25%' }}><strong>Name:</strong></td>
                    <td style={{ width: '75%' }}>{selectedBooking.created_by_name || 'System'}</td>
                  </tr>
                  <tr>
                    <td><strong>Staff ID:</strong></td>
                    <td>{selectedBooking.created_by_id || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>Dept:</strong></td>
                    <td>{selectedBooking.created_by_department || 'N/A'}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Signature Section - Minimal */}
            <div style={{ marginTop: '15px', paddingTop: '10px' }}>
              <table style={{ width: '100%', fontSize: '11px' }}>
                <tbody>
                  <tr>
                    <td align="center" style={{ width: '50%', paddingTop: '30px' }}>
                      <div style={{ borderTop: '1px solid #333', width: '90%', margin: '0 auto', paddingTop: '5px' }}>
                        <p style={{ margin: 0, fontSize: '10px', color: '#666' }}>Guest Signature</p>
                      </div>
                    </td>
                    <td align="center" style={{ width: '50%', paddingTop: '30px' }}>
                      <div style={{ borderTop: '1px solid #333', width: '90%', margin: '0 auto', paddingTop: '5px' }}>
                        <p style={{ margin: 0, fontSize: '10px', color: '#666' }}>Staff Signature</p>
                        <p style={{ margin: '2px 0 0', fontSize: '9px', color: '#999' }}>{selectedBooking.created_by_name || 'Authorized Staff'}</p>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Footer - Compact */}
            <div style={{ 
              textAlign: 'center', 
              padding: '10px', 
              marginTop: '15px',
              borderTop: '1px solid #ddd',
              fontSize: '9px', 
              color: '#666'
            }}>
              <p style={{ margin: '2px 0' }}>Thank you for choosing {HOTEL_NAME || 'our hotel'}</p>
              <p style={{ margin: '2px 0' }}>For inquiries: {HOTEL_PHONE}</p>
              <p style={{ margin: '2px 0' }}>Generated: {new Date().toLocaleString()}</p>
            </div>
          </div>
        </div>
      )}
    </Box>
  </DialogContent>
  <DialogActions>
    <Button onClick={() => setPrintBookingDialogOpen(false)}>Cancel</Button>
    <Button variant="contained" startIcon={<Print />} onClick={handlePrintIndividualBooking}>
      Print Receipt
    </Button>
  </DialogActions>
</Dialog>
          {/* Hidden printable content for full report */}
          <Box sx={{ display: 'none' }}>
            <Box ref={printRef}>
              <div className="header">
                <h1>Accommodation Sales Report</h1>
                <p>{getDateRangeText()}</p>
                <p>Generated on: {new Date().toLocaleDateString()}</p>
              </div>
              
              <div className="stats-grid">
                <div className="stat-card"><h3>₦{stats.totalRevenue.toLocaleString()}</h3><p>Total Revenue</p></div>
                <div className="stat-card"><h3>₦{stats.totalPaid.toLocaleString()}</h3><p>Total Collected</p></div>
                <div className="stat-card"><h3>{stats.totalBookings}</h3><p>Total Bookings</p></div>
                <div className="stat-card"><h3>{stats.confirmedBookings}</h3><p>Confirmed</p></div>
              </div>

              <table>
                <thead>
                  <tr>
                    <th>Booking #</th><th>Guest</th><th>Room</th><th>Check-in</th><th>Check-out</th><th>Amount</th><th>Discount</th><th>Status</th><th>Payment</th><th>Staff</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBookings.map((booking) => (
                    <tr key={booking.id}>
                      <td>{booking.booking_number}</td>
                      <td>{booking.guest_name}</td>
                      <td>{booking.room_type} ({booking.room_number})</td>
                      <td>{booking.check_in_date}</td>
                      <td>{booking.check_out_date}</td>
                      <td>₦{parseFloat(booking.total_amount).toLocaleString()}</td>
                      <td>{booking.discount_amount ? `₦${booking.discount_amount}` : '-'}</td>
                      <td>{booking.booking_status}</td>
                      <td>{booking.payment_status}</td>
                      <td>{booking.created_by_name || 'System'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="footer"><p>Report generated on {new Date().toLocaleString()}</p></div>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AccommodationSalesReport;